"use client";

import {
  CopyButton,
  ErrorNotice,
  Notice,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import {
  CARD,
  TABLE,
  TD,
  TH,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import type { ODDS_RATIO_CALCULATOR_STRINGS } from "@/components/tools/odds-ratio-calculator";
import { t } from "@/lib/i18n/interpolate";
import {
  type Formatter,
  fixed,
  formatP,
} from "@/lib/tool-kit/stats-research/format";
import type {
  NumberNeeded,
  RatioEstimate,
  RiskResult,
} from "@/lib/tool-kit/stats-research/risk";

/*
 * The result panel of the odds ratio & relative risk calculator. Which measures appear
 * depends on the design: a case-control table supports only the odds ratio.
 */

type Strings = typeof ODDS_RATIO_CALCULATOR_STRINGS;

export type Design = "cohort" | "caseControl" | "crossSectional";

/** 1/0.1 can arrive as 10.000000000000002; rounding up must not turn it into 11. */
const ROUNDING_SLACK = 1e-9;

interface Row {
  label: string;
  estimate: string;
  interval: string;
  p: string;
}

function MeasureTable({ rows, strings }: { rows: Row[]; strings: Strings }) {
  const head = [
    strings.columnMeasure,
    strings.columnEstimate,
    strings.columnInterval,
    strings.columnP,
  ];
  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-card">
      <table className={TABLE}>
        <thead>
          <tr>
            {head.map((cell) => (
              <th className={TH} key={cell} scope="col">
                {cell}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.label}>
              <th className={`${TD} font-medium`} scope="row">
                {row.label}
              </th>
              <td className={`${TD} font-mono`}>{row.estimate}</td>
              <td className={`${TD} font-mono`}>{row.interval}</td>
              <td className={`${TD} font-mono`}>{row.p}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function NumberNeededText({
  needed,
  strings,
  fmt,
}: {
  needed: NumberNeeded;
  strings: Strings;
  fmt: Formatter;
}) {
  if (needed.direction === "none") {
    return <p className="text-sm">{strings.nntNone}</p>;
  }
  const one = (value: number) => fixed(fmt, value, 1);
  const lead = t(
    needed.direction === "harm" ? strings.nntMore : strings.nntFewer,
    {
      value: one(needed.value),
      rounded: fmt(Math.ceil(needed.value - ROUNDING_SLACK)),
    }
  );
  const interval =
    needed.interval.kind === "bounded"
      ? t(strings.nntBounded, {
          lower: one(needed.interval.lower),
          upper: one(needed.interval.upper),
        })
      : t(strings.nntSplit, {
          benefit: one(needed.interval.towardsBenefit),
          harm: one(needed.interval.towardsHarm),
        });
  return (
    <p className="text-foreground text-sm leading-relaxed">
      {lead} {interval}
    </p>
  );
}

function labelsFor(design: Design, strings: Strings) {
  const prevalence = design === "crossSectional";
  return {
    odds: prevalence ? strings.prevalenceOddsRatio : strings.oddsRatio,
    ratio: prevalence ? strings.prevalenceRatio : strings.riskRatio,
    difference: prevalence
      ? strings.prevalenceDifference
      : strings.riskDifference,
    exposed: prevalence ? strings.prevalenceExposed : strings.riskExposed,
    unexposed: prevalence ? strings.prevalenceUnexposed : strings.riskUnexposed,
    reportOdds: prevalence ? strings.reportPor : strings.reportOr,
    reportRatio: prevalence ? strings.reportPr : strings.reportRr,
  };
}

function ratioRow(label: string, ratio: RatioEstimate, fmt: Formatter): Row {
  const three = (value: number) => fixed(fmt, value, 3);
  return {
    label,
    estimate: three(ratio.value),
    interval: `[${three(ratio.lower)}, ${three(ratio.upper)}]`,
    p: formatP(fmt, ratio.p),
  };
}

function reportPart(
  template: string,
  estimate: { value: number; lower: number; upper: number },
  fmt: Formatter
): string {
  return t(template, {
    value: fixed(fmt, estimate.value, 2),
    lower: fixed(fmt, estimate.lower, 2),
    upper: fixed(fmt, estimate.upper, 2),
  });
}

function Working({
  result,
  design,
  strings,
  fmt,
}: {
  result: RiskResult;
  design: Design;
  strings: Strings;
  fmt: Formatter;
}) {
  const or = result.oddsRatio as RatioEstimate;
  const shift = or.corrected ? 0.5 : 0;
  const rrShift = result.riskRatio?.corrected ? 0.5 : 0;
  const cell = (value: number) => fmt(value + shift);
  const four = (value: number) => fixed(fmt, value, 4);
  const three = (value: number) => fixed(fmt, value, 3);
  return (
    <div className={`${CARD} space-y-2`}>
      <h3 className={WIDGET_H3}>{strings.howCalculated}</h3>
      <p className="font-mono text-muted-foreground text-sm leading-relaxed">
        {t(strings.workingOr, {
          a: cell(result.a),
          b: cell(result.b),
          c: cell(result.c),
          d: cell(result.d),
          value: three(or.value),
        })}
      </p>
      <p className="font-mono text-muted-foreground text-sm leading-relaxed">
        {t(strings.workingOrSe, {
          se: four(or.logSe),
          lower: three(or.lower),
          upper: three(or.upper),
        })}
      </p>
      {design !== "caseControl" && result.riskRatio ? (
        <p className="font-mono text-muted-foreground text-sm leading-relaxed">
          {t(strings.workingRr, {
            r1: four(
              (result.a + rrShift) / (result.a + result.b + 2 * rrShift)
            ),
            r0: four(
              (result.c + rrShift) / (result.c + result.d + 2 * rrShift)
            ),
            value: three(result.riskRatio.value),
            se: four(result.riskRatio.logSe),
          })}
        </p>
      ) : null}
      {design !== "caseControl" && result.riskDifference ? (
        <p className="font-mono text-muted-foreground text-sm leading-relaxed">
          {t(strings.workingRd, {
            r1: four(result.riskExposed),
            r0: four(result.riskUnexposed),
            value: four(result.riskDifference.value),
            se: four(result.riskDifference.se),
          })}
        </p>
      ) : null}
    </div>
  );
}

export function RiskSummary({
  result,
  design,
  strings,
  fmt,
}: {
  result: RiskResult;
  design: Design;
  strings: Strings;
  fmt: Formatter;
}) {
  const or = result.oddsRatio;
  if (!or) {
    return <ErrorNotice>{strings.emptyMargin}</ErrorNotice>;
  }
  const labels = labelsFor(design, strings);
  const risks = design !== "caseControl";
  const rows: Row[] = [ratioRow(labels.odds, or, fmt)];
  const report = [reportPart(labels.reportOdds, or, fmt)];
  if (risks && result.riskRatio) {
    rows.push(ratioRow(labels.ratio, result.riskRatio, fmt));
    report.push(reportPart(labels.reportRatio, result.riskRatio, fmt));
  }
  const rd = result.riskDifference;
  if (risks && rd) {
    rows.push({
      label: labels.difference,
      estimate: fixed(fmt, rd.value, 3),
      interval: `[${fixed(fmt, rd.lower, 3)}, ${fixed(fmt, rd.upper, 3)}]`,
      p: "—",
    });
    report.push(reportPart(strings.reportRd, rd, fmt));
  }
  const reportText = `${report.join("; ")}.`;
  return (
    <section aria-live="polite" className="space-y-4">
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      <MeasureTable rows={rows} strings={strings} />
      {risks ? (
        <StatGrid>
          <StatCard
            label={labels.exposed}
            value={fixed(fmt, result.riskExposed, 3)}
          />
          <StatCard
            label={labels.unexposed}
            value={fixed(fmt, result.riskUnexposed, 3)}
          />
        </StatGrid>
      ) : null}
      {design === "caseControl" ? (
        <Notice>{strings.caseControlNote}</Notice>
      ) : null}
      {design === "crossSectional" ? (
        <Notice>{strings.crossSectionalNote}</Notice>
      ) : null}
      {or.corrected ? <Notice>{strings.orCorrected}</Notice> : null}
      {risks && result.riskRatio?.corrected ? (
        <Notice>{strings.rrCorrected}</Notice>
      ) : null}
      {design === "cohort" && result.numberNeeded ? (
        <div className="space-y-1">
          <h3 className={WIDGET_H3}>{strings.nntHeading}</h3>
          <NumberNeededText
            fmt={fmt}
            needed={result.numberNeeded}
            strings={strings}
          />
        </div>
      ) : null}
      <div className="flex flex-wrap items-center gap-3">
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copyResult}
          text={reportText}
        />
        <p className="font-mono text-muted-foreground text-sm">{reportText}</p>
      </div>
      <Working design={design} fmt={fmt} result={result} strings={strings} />
    </section>
  );
}
